import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { EnhancedSeoHead, FAQ } from "@appcaire/shared/seo";
import {
  generateArticleSchema,
  generateFAQSchema,
  generateBreadcrumbSchema,
  combineSchemas,
} from "@appcaire/shared/seo";
import {
  Users,
  Star,
  Clock,
  Smartphone,
  CheckCircle,
  ArrowRight,
} from "lucide-react";

const CRITERIA = [
  {
    icon: Users,
    title: "Personalkontinuitet",
    description:
      "Hur många olika personer kommer hem till dig? Hög kontinuitet ger trygghet.",
  },
  {
    icon: Clock,
    title: "Flexibilitet",
    description:
      "Kan tiderna anpassas efter dina önskemål och förändrade behov?",
  },
  {
    icon: Smartphone,
    title: "Kommunikation",
    description:
      "Hur når du och anhöriga personalen? Finns schema och tydliga kontaktvägar?",
  },
  {
    icon: Star,
    title: "Kvalitet och omdömen",
    description:
      "Vad säger brukarundersökningar och andra brukare om utföraren?",
  },
];

const QUESTIONS = [
  "Hur ofta träffar jag samma personal?",
  "Kan tiderna anpassas om mina behov ändras?",
  "Hur kommunicerar ni med mig och mina anhöriga?",
  "Hur länge har ni verkat i Nacka?",
  "Hur följer ni upp kvalitet och avvikelser?",
];

const VALJA_FAQ_ITEMS = [
  {
    question: "Hur väljer jag hemtjänst i Nacka?",
    answer:
      "När du har fått biståndsbeslut får du välja bland Nackas godkända hemtjänstutförare. Jämför kontinuitet, flexibilitet och omdömen, kontakta de du är intresserad av och meddela ditt val till biståndshandläggaren.",
  },
  {
    question: "Vad ska jag tänka på när jag väljer anordnare?",
    answer:
      "Prioritera personalkontinuitet, flexibla tider, tydlig kommunikation och källredovisade kvalitetsuppgifter. Fråga hur många olika personer som brukar komma och hur scheman och avvikelser hanteras.",
  },
  {
    question: "Kan jag byta utförare om jag inte är nöjd?",
    answer:
      "Ja. I Nacka har du kundval och kan byta hemtjänstutförare när du vill, utan att ange skäl. Kontakta din biståndshandläggare – se vår guide om att byta.",
  },
];

/**
 * Valja - Guide to choosing home care in Nacka
 *
 * SEO Focus: "välja hemtjänst Nacka", "välja hemtjänstutförare Nacka"
 */
const Valja: React.FC = () => {
  const articleSchema = useMemo(
    () =>
      generateArticleSchema({
        headline: "Välja hemtjänst i Nacka | Tips och råd",
        description:
          "Så väljer du rätt hemtjänstutförare i Nacka. Vad du ska fråga om, jämföra och tänka på innan du bestämmer dig.",
        url: "https://nackahemtjanst.se/valja",
        datePublished: "2026-07-25",
        dateModified: "2026-07-25",
      }),
    [],
  );

  const faqSchema = useMemo(
    () =>
      generateFAQSchema(
        VALJA_FAQ_ITEMS.map((item) => ({
          question: item.question,
          answer: item.answer,
        })),
        "https://nackahemtjanst.se/valja",
      ),
    [],
  );

  const breadcrumbSchema = useMemo(
    () =>
      generateBreadcrumbSchema([
        { name: "Hem", url: "https://nackahemtjanst.se/" },
        { name: "Välja hemtjänst", url: "https://nackahemtjanst.se/valja" },
      ]),
    [],
  );

  const combinedSchema = useMemo(
    () => combineSchemas([articleSchema, faqSchema, breadcrumbSchema]),
    [articleSchema, faqSchema, breadcrumbSchema],
  );

  return (
    <>
      <EnhancedSeoHead
        title="Välja hemtjänst i Nacka | Tips och råd"
        description="Så väljer du rätt hemtjänstutförare i Nacka. Vad du ska fråga om, jämföra och tänka på innan du bestämmer dig."
        canonicalUrl="https://nackahemtjanst.se/valja"
        ogType="article"
        keywords={[
          "välja hemtjänst Nacka",
          "välja hemtjänstutförare Nacka",
          "jämföra hemtjänst Nacka",
          "kundval hemtjänst Nacka",
        ]}
        contentClassification="Consumer Guide"
        targetAudience="seniors, families"
        coverage="Sweden"
        publishDate="2026-07-25T00:00:00+02:00"
        modifiedDate="2026-07-25T00:00:00+02:00"
        author="Nacka Hemtjänst"
        schemaMarkup={combinedSchema ?? undefined}
        entities={[{ type: "Place", name: "Nacka" }]}
      />

      <section className="relative bg-gradient-to-b from-emerald-50 via-white to-white py-16 md:py-24 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-100/20 via-transparent to-blue-100/20" />
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <div className="bg-glass-white backdrop-blur-glass border border-glass-border rounded-2xl p-6 inline-block mb-6 shadow-glass-light">
            <Users className="w-12 h-12 text-emerald-600" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-surface-900 tracking-tight mb-6">
            Välja hemtjänst i Nacka
          </h1>
          <p className="text-xl text-surface-600 max-w-2xl mx-auto leading-relaxed">
            När du har fått beslut om hemtjänst väljer du fritt bland Nackas
            godkända utförare. Börja med att jämföra kontinuitet, tider och
            omdömen – sedan meddelar du ditt val till biståndshandläggaren.
          </p>
        </div>
      </section>

      <section className="py-12 px-4 bg-white">
        <div className="max-w-4xl mx-auto bg-glass-white backdrop-blur-glass border border-glass-border rounded-2xl p-6 md:p-8 shadow-glass-light text-surface-700 leading-relaxed space-y-4">
          <p>
            Har du ännu inte ansökt? Börja med att{" "}
            <Link
              to="/ansoka"
              className="text-emerald-600 font-medium hover:underline"
            >
              ansöka om hemtjänst
            </Link>
            . Jämför anordnare via{" "}
            <Link
              to="/hitta-hemtjanst"
              className="text-emerald-600 font-medium hover:underline"
            >
              hitta hemtjänst
            </Link>
            . I Nacka gäller kundval – du kan senare{" "}
            <Link
              to="/byta"
              className="text-emerald-600 font-medium hover:underline"
            >
              byta utförare
            </Link>
            ; mer under{" "}
            <Link
              to="/rattigheter"
              className="text-emerald-600 font-medium hover:underline"
            >
              dina rättigheter
            </Link>
            . Läs också hur du tolkar{" "}
            <Link
              to="/recensioner"
              className="text-emerald-600 font-medium hover:underline"
            >
              recensioner och omdömen
            </Link>
            .
          </p>
        </div>
      </section>

      <section className="py-16 px-4 bg-gradient-to-b from-white to-surface-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-surface-900 mb-10 text-center">
            Fyra saker att jämföra
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {CRITERIA.map((item) => (
              <div
                key={item.title}
                className="bg-glass-white backdrop-blur-glass border border-glass-border rounded-xl p-6 shadow-glass-light flex gap-4"
              >
                <div className="p-3 rounded-lg bg-emerald-100 flex-shrink-0">
                  <item.icon className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <h3 className="font-bold text-surface-900 mb-2 text-lg">
                    {item.title}
                  </h3>
                  <p className="text-surface-600">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-4 bg-white">
        <div className="max-w-4xl mx-auto bg-glass-white backdrop-blur-glass border border-glass-border rounded-2xl p-8 shadow-glass-light">
          <h2 className="text-2xl font-bold text-surface-900 mb-4">
            Frågor att ställa till utföraren
          </h2>
          <ul className="space-y-3">
            {QUESTIONS.map((q) => (
              <li key={q} className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <span className="text-surface-700">{q}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="py-16 px-4 bg-gradient-to-b from-surface-50 to-white">
        <div className="max-w-4xl mx-auto">
          <FAQ items={VALJA_FAQ_ITEMS} title="Vanliga frågor om att välja" />
        </div>
      </section>

      <section className="py-16 px-4 bg-white">
        <div className="max-w-4xl mx-auto text-center bg-glass-white backdrop-blur-glass border border-glass-border rounded-2xl p-8 shadow-glass-light">
          <h2 className="text-3xl font-bold text-surface-900 mb-6">
            Jämför anordnare i Nacka
          </h2>
          <p className="text-xl text-surface-600 mb-8 max-w-2xl mx-auto">
            Se godkända hemtjänstutförare och hitta en som passar dig.
          </p>
          <Link
            to="/hitta-hemtjanst"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-emerald-600 text-white text-lg font-semibold shadow-lg hover:bg-emerald-700 transition-all"
          >
            Hitta hemtjänst
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </>
  );
};

export default Valja;
